import "./trade.less";

import styled from "@emotion/styled";
import { useMatomo } from "@jonkoops/matomo-tracker-react";
import * as Sentry from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { TransactionVersion } from "@solana/web3.js";
import { convert, STEP_MINT } from "@stepfinance/common";
import { BaseButton, imageFromPlatformName, transparentWhite } from "@stepfinance/react";
import { Button as AntButton } from "antd";
import type { Dispatch, FC, ReactNode, SetStateAction } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { ReactComponent as SwitchSVG } from "../../assets/switch-assets.svg";
import { useAccountsContextRefresher } from "../../contexts/accounts";
import { useAppConfig } from "../../contexts/app/config";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLanguageContext } from "../../contexts/language/language";
import { useLiquidity } from "../../contexts/liquidity";
import { useMarkets } from "../../contexts/market";
import { SwapRoute, useSwapContextState } from "../../contexts/swap";
import { useUserStakedAccess } from "../../hooks/utils/useUserStakedAccess";
import { useConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import type { Wallet } from "../../types/wallet";
import type { JupiterRoute } from "../../utils/jupiter/api";
import { JupiterSwapHandler } from "../../utils/jupiter/api";
import { notify } from "../../utils/notifications";
import { PoolOperation } from "../../utils/pools";
import { PRICE_IMPACT_BANNER_WARNING_THRESHOLD, PRICE_IMPACT_THRESHOLD } from "../../utils/pools/utils";
import { getTokenByMint, getTokenName } from "../../utils/tokenInfo";
import { handlePreSendTxnError } from "../../utils/transactions";
import { formatStringWithPlaceholders } from "../../utils/utils";
import { AmountInput } from "../AmountInput/AmountInput";
import { generateActionLabel, POOL_NOT_AVAILABLE } from "../labels";
import { SwapTokenWarning } from "../SwapTokenWarning/SwapTokenWarning";
import { OutTokenDisplay } from "./OutTokenDisplay";
import { RouteSelector } from "./RouteSelector";
import { SwapAlert } from "./SwapAlert";
import { SwapTradeInfo } from "./SwapTradeInfo";
import { WrapUnwrapButton } from "./WrapUnwrapButton";

const SwitchButton = styled(AntButton)`
  margin: 10px 0px;
  height: 32px;
  width: 32px;
  padding: 0;
`;

export const TradeButton = styled(BaseButton)`
  width: 100%;
  padding: 10px;
  font-size: 16px;
  line-height: 22px;
  height: 60px;
  letter-spacing: 0em;
`;

export const TradeEntry = () => {
  const [pendingTx, setPendingTx] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const { A, B, slippage, swapToFromMints, swapRouteContext, setLastTypedAccount, setPoolOperation } =
    useSwapContextState();
  const stakedAccess = useUserStakedAccess();

  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState<JupiterRoute[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<JupiterRoute>();
  const [refreshKey, setRefreshKey] = useState(0);
  const { transactionVersion } = useAppConfig();
  const isWrapping = swapRouteContext.type === SwapRoute.WRAP;
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    currentLanguageData: { common },
  } = useLanguageContext();

  const filteredRoutes = useMemo(() => {
    return [...routes]
      .sort((a, b) => b.outAmountWithSlippage - a.outAmountWithSlippage)
      .filter((route) => !route.marketInfos.some((info) => info.notEnoughLiquidity));
  }, [routes]);

  useEffect(() => {
    const fetchJupiterData = async () => {
      const inputAmount = A.convertAmount();
      if (A.amount.length === 0 || inputAmount === 0) {
        return;
      }
      setLoading(true);
      const feeBps = stakedAccess ? 10 : 15;
      const jupRoutes = await JupiterSwapHandler.getRoutes(
        A.mintAddress,
        B.mintAddress,
        inputAmount,
        slippage,
        feeBps,
        transactionVersion,
      );
      setTimeout(() => {
        setRoutes(jupRoutes);
        if (jupRoutes.length) {
          setSelectedRoute(jupRoutes[0]);
          B.setAmount(convert(jupRoutes[0].outAmount, B.mint?.decimals).toString());
        } else {
          setSelectedRoute(undefined);
        }
        setLoading(false);
      }, 0);
    };

    const timer = setTimeout(() => {
      void fetchJupiterData();
    }, 750);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [A.amount, A.mintAddress, B.mintAddress, slippage, refreshKey, transactionVersion]);

  const setInputAmount = (value: string) => {
    if (A.amount !== value) {
      setLastTypedAccount(A.mintAddress);
    }
    setPoolOperation(PoolOperation.SwapGivenInput);
    A.setAmount(value);
  };

  return (
    <>
      <TradeContainer>
        <div>
          <SwapTokenWarning
            mintA={getTokenByMint(A.mintAddress.toBase58())}
            mintB={getTokenByMint(B.mintAddress.toBase58())}
          />
          <AmountInput
            amount={A.amount}
            mint={A.mintAddress}
            mintDecimals={A.account?.tokenAmount.decimals}
            inputRef={inputRef}
            onAmountChange={(event, _value) => setInputAmount(event.currentTarget.value)}
            onMintChange={(item) => {
              setLastTypedAccount(item);
              A.setMint(item);
            }}
            disabledMintFromTokenList={B.mintAddress}
            showHalfButton
            showMaxButton
            headerText={formatStringWithPlaceholders(common.amountInputLabels.youWill, common.amountInputLabels.pay)}
          />
          <SwitchButtonContainer>
            <SwitchButton type="link" size="large" onClick={swapToFromMints}>
              <SwitchSVG height={32} width={32} />
            </SwitchButton>
          </SwitchButtonContainer>
          <OutTokenDisplay isWrapping={isWrapping} onMintChange={(item) => B.setMint(item)} A={A} B={B} />
          {routes.length > 0 && A.convertAmount() > 0 && A.amount.length > 0 && !isWrapping && (
            <RouteSelector A={A} B={B} routes={filteredRoutes} setSelectedRoute={setSelectedRoute} />
          )}
        </div>
      </TradeContainer>
      <SwapButtonContainer>
        {swapRouteContext.type === SwapRoute.WRAP ? (
          <WrapUnwrapButton
            pendingTx={pendingTx}
            setPendingTx={setPendingTx}
            pendingConfirmation={pendingConfirmation}
            setPendingConfirmation={setPendingConfirmation}
          />
        ) : (
          <AMMSwapButton
            loading={loading}
            pendingTx={pendingTx}
            setPendingTx={setPendingTx}
            pendingConfirmation={pendingConfirmation}
            setPendingConfirmation={setPendingConfirmation}
            route={selectedRoute}
            refreshRoute={() => setRefreshKey((prevKey) => prevKey + 1)}
            stakedAccess={stakedAccess}
            transactionVersion={transactionVersion}
          />
        )}
      </SwapButtonContainer>
    </>
  );
};

const SwitchButtonContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SwapButtonContainer = styled.div`
  margin-top: 20px;
  .ant-btn {
    border-radius: 8px;
  }
`;

const TradeContainer = styled.div`
  background-color: #141414;
  padding: 20px;
  border-radius: 0px 8px 8px 8px;
`;

const FooterContainer = styled.div`
  margin: 10px 0px 20px 0px;
  text-align: center;
  span {
    color: ${transparentWhite};
    strong {
      color: white;
    }
  }
`;

export type AMMSwapButtonProps = {
  loading: boolean;
  pendingTx: boolean;
  setPendingTx: (val: boolean) => void;
  pendingConfirmation: boolean;
  setPendingConfirmation: Dispatch<SetStateAction<boolean>>;
  route?: JupiterRoute;
  refreshRoute: () => void;
  stakedAccess: boolean;
  transactionVersion: TransactionVersion;
};

const AMMSwapButton: FC<AMMSwapButtonProps> = ({
  loading,
  pendingTx,
  setPendingTx,
  pendingConfirmation,
  setPendingConfirmation,
  route,
  refreshRoute,
  stakedAccess,
  transactionVersion,
}) => {
  const { trackEvent } = useMatomo();

  const wallet = useConnectedWallet();
  const { connected, watching } = useEnrichedWallet();
  const { connection } = useConnection();
  const { A, B, slippage } = useSwapContextState();

  const refreshAccounts = useAccountsContextRefresher();
  const { refreshPools } = useLiquidity();
  const priceImpact = route ? route.priceImpactPct : 0;

  const inToken = getTokenByMint(A.mintAddress.toBase58());
  const outToken = getTokenByMint(B.mintAddress.toBase58());
  const outPerInRate = convert(route?.outAmount, outToken?.decimals) / convert(route?.inAmount, inToken?.decimals);

  const { priceInUSD, marketsInitialised } = useMarkets();

  const {
    currentLanguageData: {
      common,
      pages: {
        swapLP: {
          poweredByJupiter,
          swapAlert,
          swapButtonLabels,
          notifications: swapNotifications,
          swapEfficiencyInfo: { coingeckoComparisons },
        },
      },
    },
  } = useLanguageContext();

  const marketRate = useMemo(() => {
    if (!priceInUSD || !marketsInitialised) {
      return null;
    }
    const A_PRICE = priceInUSD(A.mintAddress.toBase58()) ?? 0;
    const B_PRICE = priceInUSD(B.mintAddress.toBase58()) ?? 0;
    const rate = A_PRICE / B_PRICE;
    if (rate <= 0 || isNaN(rate) || !isFinite(rate)) {
      return null;
    }
    return rate;
  }, [A.mintAddress, B.mintAddress, marketsInitialised, priceInUSD]);

  const sendSwapTransaction = async (connectedWallet: Wallet) => {
    try {
      if ((!Number.isNaN(priceImpact) && priceImpact > PRICE_IMPACT_THRESHOLD) || !route) {
        return;
      }

      const notificationStrings = { ...common.notifications, ...swapNotifications };
      const jupiterSwapHandler = new JupiterSwapHandler({
        connection,
        wallet: connectedWallet,
        route,
        transactionVersion,
        notificationStrings,
        inputToken: {
          mint: A.mintAddress,
          amount: Number(A.amount),
          symbol: getTokenByMint(A.mintAddress.toBase58())!.symbol,
        },
        outputToken: {
          mint: B.mintAddress,
          amount: Number(B.amount),
          symbol: getTokenByMint(B.mintAddress.toBase58())!.symbol,
        },
      });
      setPendingTx(true);
      if (transactionVersion === 0) {
        await jupiterSwapHandler.executeVersion0Transaction();
      } else {
        await jupiterSwapHandler.executeLegacyTransaction();
      }

      trackEvent({
        category: "Swaps",
        action: "swap",
        name: `${getTokenName(A.mintAddress)}/${getTokenName(B.mintAddress)}`,
        value: parseFloat(A.amount),
      });
    } catch (err) {
      Sentry.captureException(err);
      handlePreSendTxnError(err as Error, "Swap error");
    } finally {
      setPendingTx(false);
      setPendingConfirmation(false);
      refreshAccounts();
      void refreshPools();
    }
  };

  let AMMButtonLabel: string | ReactNode[];
  if (loading) {
    AMMButtonLabel = swapButtonLabels.findingRoute;
  } else if (!connected) {
    AMMButtonLabel = swapButtonLabels.connectWalletToSwap;
  } else if (!A.sufficientBalance() && A.amount.length !== 0) {
    AMMButtonLabel = formatStringWithPlaceholders(swapButtonLabels.insufficientBalance, getTokenName(A.mintAddress));
  } else if (A.amount.length === 0 || A.convertAmount() === 0) {
    AMMButtonLabel = swapButtonLabels.inputAmount;
  } else if (!route && (A.mintAddress.equals(STEP_MINT) || B.mintAddress.equals(STEP_MINT))) {
    AMMButtonLabel = POOL_NOT_AVAILABLE(getTokenName(A.mintAddress), getTokenName(B.mintAddress));
  } else if (!route) {
    AMMButtonLabel = formatStringWithPlaceholders(
      swapButtonLabels.noRoute,
      getTokenName(A.mintAddress),
      getTokenName(B.mintAddress),
    );
  } else {
    AMMButtonLabel = generateActionLabel(
      pendingConfirmation ? swapButtonLabels.awaitingTxn : pendingTx ? swapButtonLabels.approveTxn : common.swap,
      connected,
      A,
      B,
      true,
      priceImpact * 100,
      watching,
    );
  }

  const marketRateVsRouteRate = marketRate ? (marketRate - outPerInRate) / marketRate : 0;

  let alertText: ReactNode = "";
  if (marketRateVsRouteRate >= 0.05) {
    alertText = formatStringWithPlaceholders(
      coingeckoComparisons.moreExpensive,
      (marketRateVsRouteRate * 100).toFixed(1),
    );
  } else if (priceImpact * 100 >= PRICE_IMPACT_BANNER_WARNING_THRESHOLD) {
    alertText = formatStringWithPlaceholders(swapAlert.priceImpactWarning, (priceImpact * 100).toFixed(1) + "%");
  }

  return (
    <>
      {alertText !== "" && <SwapAlert smallerAmountText={swapAlert.swapWithASmallerAmount} messageText={alertText} />}
      <TradeButton
        loading={loading}
        onClick={() => {
          if (!wallet) {
            notify({
              type: "error",
              message: swapNotifications.swapCancelled.message,
              description: swapNotifications.swapCancelled.description,
            });
            return;
          }
          void sendSwapTransaction(wallet);
        }}
        disabled={
          !connected ||
          (connected &&
            (watching ||
              pendingTx ||
              !A.account ||
              !B.mintAddress ||
              loading ||
              A.account === B.account ||
              !A.sufficientBalance() ||
              A.convertAmount() === 0 ||
              !route ||
              (!Number.isNaN(priceImpact) && priceImpact * 100 > PRICE_IMPACT_THRESHOLD)))
        }
      >
        {AMMButtonLabel}
      </TradeButton>
      <FooterContainer>
        <span>
          {formatStringWithPlaceholders(
            poweredByJupiter,
            <>
              <JupiterLogo src={imageFromPlatformName("Jupiter")} alt="" width={22} height={22} />
              <strong>Jupiter</strong>
            </>,
          )}
          &nbsp;&nbsp;
        </span>
      </FooterContainer>
      {route && A.convertAmount() > 0 && A.amount.length > 0 && (
        <>
          <SwapTradeInfo
            route={route}
            slippageTolerance={slippage}
            loading={loading}
            refreshRoute={refreshRoute}
            stakedAccess={stakedAccess}
            inTokenInfo={inToken}
            outTokenInfo={outToken}
            marketRate={marketRate}
            outPerInRate={outPerInRate}
          />
        </>
      )}
    </>
  );
};

const JupiterLogo = styled.img`
  margin: 0 6px 0 5px;
`;
